import { useState, useCallback, useEffect } from "react";
import { Modal, Checkbox } from "antd";
import { Trans, useTranslation } from "react-i18next";
import CommonButton from "@components/CommonButton";

interface UploadAgreementModalProps {
  open: boolean;
  agreementItems: string[];
  onAccept: () => void;
  onCancel: () => void;
}

const UploadAgreementModal = ({ open, agreementItems, onAccept, onCancel }: UploadAgreementModalProps) => {
  const { t } = useTranslation();
  const [checkedItems, setCheckedItems] = useState<boolean[]>([]);

  useEffect(() => {
    if (open && agreementItems.length > 0) {
      setCheckedItems(Array.from({ length: agreementItems.length }, () => false));
    }
  }, [open, agreementItems.length]);

  const allChecked =
    agreementItems.length > 0 && checkedItems.length === agreementItems.length && checkedItems.every(Boolean);

  const handleCheckChange = useCallback((index: number, checked: boolean) => {
    setCheckedItems((prev) => {
      const newChecked = [...prev];
      newChecked[index] = checked;
      return newChecked;
    });
  }, []);

  const handleAccept = () => {
    if (allChecked) {
      onAccept();
      setCheckedItems(Array.from({ length: agreementItems.length }, () => false));
    }
  };

  const handleCancel = () => {
    onCancel();
    setCheckedItems(Array.from({ length: agreementItems.length }, () => false));
  };

  if (!open) return null;

  return (
    <Modal open={open} footer={null} closable={false} width={450} centered destroyOnHidden>
      <div>
        <h3 className="text-[16px] font-semibold text-kds-fg m-0 mb-3">
          <Trans>Media upload agreement</Trans>
        </h3>
        <p className="text-[14px] text-kds-fg-soft m-0 mb-5 leading-normal">
          <Trans>Confirm you’ve read and agreed to the following by checking the box on each item below.</Trans>
        </p>

        <div className="flex flex-col gap-4 mb-6">
          {agreementItems.length === 0 ? (
            <span className="text-[14px] text-kds-fg-subtle">
              <Trans>Loading...</Trans>
            </span>
          ) : (
            agreementItems.map((item, index) => (
              <Checkbox
                key={index}
                checked={checkedItems[index]}
                onChange={(e) => handleCheckChange(index, e.target.checked)}
              >
                <span className="text-[14px] text-kds-fg-soft leading-normal">{t(item)}</span>
              </Checkbox>
            ))
          )}
        </div>

        <div className="flex items-center justify-end gap-[12px]">
          <CommonButton type="default" onClick={handleCancel}>
            <Trans>Cancel</Trans>
          </CommonButton>
          <CommonButton type="primary" disabled={!allChecked} onClick={handleAccept}>
            <Trans>Accept</Trans>
          </CommonButton>
        </div>
      </div>
    </Modal>
  );
};

export default UploadAgreementModal;
